import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop Component
 * 
 * Automatically scrolls to top of page on route changes.
 * Handles both desktop and mobile browsers.
 * 
 * Exceptions:
 * - Does NOT scroll when navigating to anchor links (hash in URL)
 * - Preserves scroll position when using browser back/forward buttons
 *   (handled by browser's native behavior)
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Don't scroll if there's a hash (anchor link on same page)
    // The browser will handle scrolling to the anchor
    if (hash) {
      // If there's a hash, let the browser handle it naturally
      // But add a small delay to ensure the element exists
      const element = document.getElementById(hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
      return;
    }

    // Scroll to top for regular page navigation
    // Using both methods for maximum browser compatibility
    
    // Method 1: Instant scroll to top (best for most cases)
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant' // Use 'instant' for immediate response, 'smooth' for animation
    });
    
    // Method 2: Fallback for older browsers and mobile Safari
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0; // For Safari compatibility
    
    // Method 3: requestAnimationFrame fallback for edge cases
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });

  }, [pathname, hash]);

  return null; // This component doesn't render anything
};

export default ScrollToTop;
